export const R6_TOP_LIGHT_PAGE_INDICES = Object.freeze([5, 6, 7]);
export const R6_SWITCH_LIGHT_PAGE_INDICES = R6_TOP_LIGHT_PAGE_INDICES;

function clone(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

function pageIndex(page) {
  return Number(page?.page_index);
}

function validValue(value) {
  if (value === true) return 1;
  if (value === false) return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 2;
}

function isIgnored(value) {
  return value !== undefined && validValue(value) === 2;
}

export function getR6Page(data, targetIndex) {
  const index = Number(targetIndex);
  return (Array.isArray(data?.page_data) ? data.page_data : []).find(
    (page) => pageIndex(page) === index
  );
}

export function getR6LightPageIndices() {
  return [...R6_TOP_LIGHT_PAGE_INDICES];
}

export function getR6SwitchLightPageIndices() {
  return [...R6_SWITCH_LIGHT_PAGE_INDICES];
}

export function hasR6ProfileData(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const hasPages =
    Array.isArray(value.page_data) &&
    value.page_data.some(
      (page) =>
        page &&
        typeof page === "object" &&
        !Array.isArray(page) &&
        [
          "valid",
          "lightness",
          "speed_ms",
          "color",
          "frames",
          "keyframes",
          "word_page",
          "spotlight_frames",
        ].some((key) => Object.prototype.hasOwnProperty.call(page, key))
    );
  const hasKeyLayer =
    Array.isArray(value.key_layer?.layer_data) &&
    (value.key_layer.layer_data.length > 0 ||
      validValue(value.key_layer.valid) === 1);
  return hasPages || hasKeyLayer;
}

function ignoredPage(page, index) {
  const source = clone(page) || emptyPage(index);
  const ignored = (section = {}) => ({ ...section, valid: 2 });
  return {
    ...source,
    page_index: source.page_index ?? index,
    valid: 2,
    word_page: ignored(source.word_page),
    frames: ignored(source.frames),
    keyframes: ignored(source.keyframes),
    ...(source.spotlight_frames
      ? { spotlight_frames: ignored(source.spotlight_frames) }
      : {}),
  };
}

export function prepareR6ImportProfile(
  baseData,
  uploadedData,
  { hasPageData = true, hasKeyLayer = true, keyLayerHasValid = true } = {}
) {
  const result = clone(uploadedData) || {};
  const basePages = Array.isArray(baseData?.page_data)
    ? baseData.page_data
    : [];
  const pages = (Array.isArray(result.page_data) ? result.page_data : []).map(
    (page, index) => {
      if (!page || typeof page !== "object" || Array.isArray(page)) {
        throw new TypeError("R6 JSON page_data entries must be objects");
      }
      const indexValue = Number(page.page_index ?? index);
      if (!Number.isInteger(indexValue) || indexValue < 0) {
        throw new TypeError(
          "R6 JSON page_index must be a non-negative integer"
        );
      }
      return { ...page, page_index: indexValue };
    }
  );
  result.page_data = pages;
  const basePageCount = Number(baseData?.page_num) || basePages.length;
  const declaredPageCount = Number(result.page_num);
  const highestPage = pages.reduce(
    (highest, page, index) =>
      Math.max(highest, Number(page?.page_index ?? index)),
    -1
  );
  const pageCount = Math.max(
    Number.isInteger(declaredPageCount) && declaredPageCount > 0
      ? declaredPageCount
      : 0,
    basePageCount,
    highestPage + 1
  );
  if (!Number.isInteger(pageCount) || pageCount < 1 || pageCount > 0xff) {
    throw new RangeError("R6 JSON page count is invalid");
  }

  result.page_num = pageCount;
  result.key_layer = {
    valid: 0,
    layer_num: 0,
    layer_data: [],
    ...result.key_layer,
  };
  if (
    !hasKeyLayer ||
    (!keyLayerHasValid && !result.key_layer.layer_data.length)
  ) {
    result.key_layer.valid = 2;
  } else if (!keyLayerHasValid && result.key_layer.layer_data.length) {
    result.key_layer.valid = 1;
  }

  if (!hasPageData) {
    const pageSources = basePages.length
      ? basePages
      : Array.from({ length: pageCount }, (_, index) => emptyPage(index));
    result.page_data = pageSources.map(ignoredPage);
  }

  const uploadedIndexes = new Set(
    result.page_data.map((page, index) => Number(page?.page_index ?? index))
  );
  const isSparse = Array.from({ length: pageCount }, (_, index) => index).some(
    (index) => !uploadedIndexes.has(index)
  );
  return {
    data: result,
    partial:
      !hasPageData || !hasKeyLayer || isSparse || isR6PartialProfile(result),
  };
}

export function prepareR6SelectionDownloadData(data, selection = {}) {
  const result = clone(data) || {};
  const led = Array.isArray(selection.led) ? selection.led : [];
  const axis = Array.isArray(selection.axis) ? selection.axis : [];
  const pages = Array.isArray(result.page_data) ? result.page_data : [];

  result.page_data = pages.map((page, index) => {
    const pageIndexValue = Number(page?.page_index ?? index);
    const position = R6_TOP_LIGHT_PAGE_INDICES.indexOf(pageIndexValue);
    if (position < 0) return page;
    return {
      ...page,
      frames: {
        ...(page.frames || {}),
        valid: led[position] ? 1 : 2,
      },
      keyframes: {
        ...(page.keyframes || {}),
        valid: axis[position] ? 1 : 2,
      },
    };
  });
  result.key_layer = {
    ...(result.key_layer || {}),
    valid: selection.keyLayer ? 1 : 2,
  };
  return result;
}

export function buildR6SyncSelectionPlan(selection = {}, pageData = []) {
  const pageFeatures = new Map();
  const addPageFeatures = (indices, selected, features) => {
    indices.forEach((pageIndex, position) => {
      if (!selected[position]) return;
      const enabled = pageFeatures.get(pageIndex) || new Set();
      features.forEach((feature) => enabled.add(feature));
      pageFeatures.set(pageIndex, enabled);
    });
  };

  addPageFeatures(getR6LightPageIndices(), selection.led || [], ["pageRgb"]);
  addPageFeatures(getR6SwitchLightPageIndices(), selection.axis || [], [
    "axisRgb",
  ]);

  // A lighting transaction must keep every profile page switchable.
  if (pageFeatures.size) {
    (Array.isArray(pageData) ? pageData : []).forEach((page, index) => {
      const indexValue = Number(page?.page_index ?? index);
      if (!Number.isFinite(indexValue)) return;
      const enabled = pageFeatures.get(indexValue) || new Set();
      enabled.add("pageControl");
      pageFeatures.set(indexValue, enabled);
    });
  }

  const pageFeatureScope = {};
  pageFeatures.forEach((features, pageIndex) => {
    pageFeatureScope[pageIndex] = [...features];
  });
  const featureSet = new Set();
  pageFeatures.forEach((features) => {
    features.forEach((feature) => featureSet.add(feature));
  });
  const featureScope = [...featureSet];
  if (selection.keyLayer) featureScope.push("keyLayer");
  return { featureScope, pageFeatureScope };
}

export function selectR6ProfileData(data, selection = {}) {
  const selectedPages = new Map();
  const addPage = (targetIndex, fields) => {
    const page = getR6Page(data, targetIndex);
    if (!page) return;
    const selected = selectedPages.get(targetIndex) || {
      page_index: pageIndex(page),
    };
    ["valid", "lightness", "speed_ms", "color"].forEach((key) => {
      if (key in page) selected[key] = clone(page[key]);
    });
    fields.forEach((key) => {
      if (key in page) selected[key] = clone(page[key]);
    });
    selectedPages.set(targetIndex, selected);
  };

  getR6LightPageIndices().forEach((pageIndex, position) => {
    if (selection.led?.[position]) addPage(pageIndex, ["frames"]);
  });
  getR6SwitchLightPageIndices().forEach((pageIndex, position) => {
    if (selection.axis?.[position]) {
      addPage(pageIndex, ["keyframes"]);
    }
  });

  const result = {
    page_data: (Array.isArray(data?.page_data) ? data.page_data : [])
      .filter((page, index) => {
        const indexValue = pageIndex(page);
        return selectedPages.has(
          Number.isFinite(indexValue) ? indexValue : index
        );
      })
      .map((page, index) => {
        const indexValue = pageIndex(page);
        return clone(
          selectedPages.get(Number.isFinite(indexValue) ? indexValue : index)
        );
      }),
  };
  ["product_info", "page_num"].forEach((key) => {
    if (data && key in data) result[key] = clone(data[key]);
  });
  if (selection.keyLayer && data?.key_layer) {
    result.key_layer = clone(data.key_layer);
    [
      "tab_key",
      "tab_key_num",
      "macro_key",
      "macro_key_num",
      "MACRO_key",
      "MACRO_key_num",
      "exchange_num",
      "exchange_key",
      "Fn_key_num",
      "Fn_key",
      "swap_key_num",
      "swap_key",
    ].forEach((key) => {
      if (key in data) result[key] = clone(data[key]);
    });
  }
  return result;
}

export function toLedStripFrames(page) {
  return (page?.frames?.frame_data || []).map((frame) => ({
    // Device JSON stores rows; LEDStrip renders five LEDs per column.
    frameRGB: (frame.frame_RGB || []).map(
      (_, index, rgb) => rgb[(index % 5) * 40 + Math.floor(index / 5)]
    ),
  }));
}

export function toR6LedFrames(frames) {
  return (Array.isArray(frames) ? frames : []).map((frame, index) => ({
    frame_index: index,
    frame_RGB: (frame?.frameRGB || []).map(
      (_, index, rgb) => rgb[(index % 40) * 5 + Math.floor(index / 40)]
    ),
  }));
}

function mergePage(basePage, uploadedPage) {
  const base = clone(basePage) || {};
  const uploaded = clone(uploadedPage) || {};
  const merged = { ...base, ...uploaded };
  const controlKeys = ["valid", "lightness", "speed_ms", "color"];
  const restoreBase = (keys) => {
    keys.forEach((key) => {
      if (key in base) merged[key] = clone(base[key]);
      else delete merged[key];
    });
  };

  if (isIgnored(uploaded.valid)) restoreBase(controlKeys);
  ["word_page", "frames", "keyframes", "spotlight_frames"].forEach(
    (section) => {
      if (
        uploaded[section] &&
        isIgnored(uploaded[section].valid) &&
        section in base
      ) {
        merged[section] = clone(base[section]);
      }
    }
  );
  merged.page_index = Number.isFinite(pageIndex(basePage))
    ? pageIndex(basePage)
    : pageIndex(uploadedPage);
  return merged;
}

function mergePages(basePages, uploadedPages) {
  const uploadedByIndex = new Map(
    uploadedPages.map((page) => [pageIndex(page), page])
  );
  const merged = basePages.map((page) => ({
    ...(uploadedByIndex.has(pageIndex(page))
      ? mergePage(page, uploadedByIndex.get(pageIndex(page)))
      : clone(page)),
  }));
  const baseIndexes = new Set(basePages.map(pageIndex));
  uploadedPages.forEach((page) => {
    if (!baseIndexes.has(pageIndex(page))) merged.push(clone(page));
  });
  return merged;
}

function emptyPage(index) {
  return {
    page_index: index,
    valid: 2,
    lightness: 0,
    speed_ms: 0,
    color: { default: false, back_rgb: "#000000", rgb: "#000000" },
    word_page: { valid: 2, word_len: 0, unicode: [] },
    frames: { valid: 2, frame_num: 0, frame_data: [] },
    keyframes: { valid: 2, frame_num: 0, frame_data: [] },
  };
}

function pageControl(page, index) {
  return {
    valid: page?.valid === undefined ? 1 : page.valid,
    page_index: page?.page_index ?? index,
    lightness: page?.lightness || 0,
    speed_ms: page?.speed_ms || 0,
    color: clone(page?.color) || {
      default: false,
      back_rgb: "#000000",
      rgb: "#000000",
    },
  };
}

function completePages(pages, pageCount) {
  const byIndex = new Map(
    pages.map((page, index) => {
      const indexValue = pageIndex(page);
      return [Number.isFinite(indexValue) ? indexValue : index, page];
    })
  );
  return Array.from(
    { length: pageCount },
    (_, index) => byIndex.get(index) || emptyPage(index)
  );
}

export function prepareR6ImportDownloadData(baseData, uploadedData) {
  const uploadedPages = Array.isArray(uploadedData?.page_data)
    ? uploadedData.page_data
    : [];
  const resolvedPageIndex = (page, index) => {
    const value = pageIndex(page);
    return Number.isFinite(value) ? value : index;
  };
  const basePages = Array.isArray(baseData?.page_data)
    ? baseData.page_data
    : [];
  if (!uploadedPages.length && !basePages.length) return uploadedData;
  const declaredPageCount = Number(uploadedData.page_num);
  const sourcePages = uploadedPages.length ? uploadedPages : basePages;
  const count = Math.max(
    Number.isInteger(declaredPageCount) && declaredPageCount > 0
      ? declaredPageCount
      : 0,
    Number(baseData?.page_num) || basePages.length,
    Math.max(...sourcePages.map(resolvedPageIndex), 0) + 1
  );
  if (!Number.isInteger(count) || count < 0 || count > 0xff) {
    throw new RangeError("R6 JSON page count exceeds the protocol limit");
  }
  const baseByIndex = new Map(
    basePages.map((page, index) => [resolvedPageIndex(page, index), page])
  );
  const uploadedByIndex = new Map();
  uploadedPages.forEach((page, index) => {
    const pageIndexValue = resolvedPageIndex(page, index);
    if (!Number.isInteger(pageIndexValue) || pageIndexValue < 0) {
      throw new TypeError("R6 JSON page_index must be a non-negative integer");
    }
    if (pageIndexValue >= count) {
      throw new RangeError("R6 JSON page_index exceeds page_num");
    }
    if (uploadedByIndex.has(pageIndexValue)) {
      throw new TypeError(`R6 JSON duplicate page_index: ${pageIndexValue}`);
    }
    uploadedByIndex.set(pageIndexValue, page);
  });

  const pageData = Array.from({ length: count }, (_, index) => {
    const uploadedPage = uploadedByIndex.get(index);
    if (uploadedPage) {
      const basePage = baseByIndex.get(index);
      const ledDataIgnored =
        isIgnored(uploadedPage.frames?.valid) &&
        isIgnored(uploadedPage.keyframes?.valid);
      return {
        ...clone(uploadedPage),
        page_index: index,
        page_control: pageControl(
          ledDataIgnored ? basePage || uploadedPage : uploadedPage,
          index
        ),
      };
    }
    const basePage = baseByIndex.get(index);
    return {
      ...emptyPage(index),
      page_control: pageControl(basePage, index),
    };
  });

  // A sparse export is a partial transaction. Missing pages stay ignored,
  // while their 02 02 values come from the current device snapshot.
  return {
    ...clone(uploadedData),
    page_num: count,
    page_data: pageData,
  };
}

export function mergeR6UploadData(baseData, uploadedData) {
  if (
    !uploadedData ||
    typeof uploadedData !== "object" ||
    Array.isArray(uploadedData)
  ) {
    throw new TypeError("R6 JSON 配置必须是对象");
  }
  const base = clone(baseData || {});
  const uploaded = clone(uploadedData);
  const basePages = Array.isArray(base.page_data) ? base.page_data : [];
  const uploadedPages = Array.isArray(uploaded.page_data)
    ? uploaded.page_data
    : [];
  const mergedPages = uploadedPages.length
    ? mergePages(basePages, uploadedPages)
    : basePages;
  const pageCount = Number(uploaded.page_num ?? base.page_num);
  const result = {
    ...base,
    ...uploaded,
    page_data:
      Number.isInteger(pageCount) && pageCount > 0
        ? completePages(mergedPages, pageCount)
        : mergedPages,
  };
  if (validValue(uploaded.key_layer?.valid) === 2) {
    [
      "key_layer",
      "tab_key",
      "tab_key_num",
      "macro_key",
      "macro_key_num",
      "MACRO_key",
      "MACRO_key_num",
      "exchange_num",
      "exchange_key",
      "Fn_key_num",
      "Fn_key",
      "swap_key_num",
      "swap_key",
    ].forEach((key) => {
      if (key in base) result[key] = clone(base[key]);
      else delete result[key];
    });
  }
  return result;
}

export function isR6PartialProfile(data) {
  const pages = Array.isArray(data?.page_data) ? data.page_data : [];
  return (
    validValue(data?.key_layer?.valid) === 2 ||
    pages.some((page) =>
      ["valid", "word_page", "frames", "keyframes", "spotlight_frames"].some(
        (section) => {
          const value =
            section === "valid" ? page?.valid : page?.[section]?.valid;
          return isIgnored(value);
        }
      )
    )
  );
}
